import { useCallback, useEffect, useRef, useState } from 'react';
import { useGoogleOAuth } from '@react-oauth/google';

const HIDDEN_BTN_WIDTH = 320;

/**
 * Botão GIS renderizado fora da tela; o botão visível dispara .click() nele.
 * Evita overlay com iframe (quebra toque no iOS e causa aparência "dupla").
 */
export function useGoogleSignInTrigger({ onSuccess, onError, locale = 'pt-BR' }) {
  const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth();
  const hiddenHostRef = useRef(null);
  const initializedRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);

  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!scriptLoadedSuccessfully || !clientId || !hiddenHostRef.current) return undefined;

    const google = window.google;
    if (!google?.accounts?.id) return undefined;

    google.accounts.id.initialize({
      client_id: clientId,
      callback: (credentialResponse) => {
        if (!credentialResponse?.credential) {
          onErrorRef.current?.();
          return;
        }
        onSuccessRef.current?.({
          credential: credentialResponse.credential,
          clientId: credentialResponse.clientId || credentialResponse.client_id,
          select_by: credentialResponse.select_by,
        });
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      itp_support: true,
      use_fedcm_for_button: false,
    });

    if (!initializedRef.current) {
      hiddenHostRef.current.innerHTML = '';
      google.accounts.id.renderButton(hiddenHostRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        width: HIDDEN_BTN_WIDTH,
        locale,
      });
      initializedRef.current = true;
    }

    const markReady = () => {
      const host = hiddenHostRef.current;
      if (!host) return false;
      return Boolean(host.querySelector('div[role="button"]') || host.querySelector('iframe'));
    };

    if (markReady()) {
      setReady(true);
      return undefined;
    }

    const observer = new MutationObserver(() => {
      if (markReady()) {
        setReady(true);
        observer.disconnect();
      }
    });
    observer.observe(hiddenHostRef.current, { childList: true, subtree: true });

    const timeout = window.setTimeout(() => {
      if (markReady()) setReady(true);
      observer.disconnect();
    }, 4000);

    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [scriptLoadedSuccessfully, clientId, locale]);

  const triggerSignIn = useCallback(() => {
    const host = hiddenHostRef.current;
    if (!host) return false;

    const roleButton = host.querySelector('div[role="button"]');
    if (roleButton) {
      roleButton.click();
      return true;
    }

    const google = window.google;
    if (google?.accounts?.id?.prompt) {
      google.accounts.id.prompt();
      return true;
    }

    return false;
  }, []);

  return { hiddenHostRef, triggerSignIn, ready: ready && scriptLoadedSuccessfully };
}
